import { getApiClient } from '../../api/client';
import type { components, operations } from '../../api/schema';
import type {
  ScorecardIncident,
  ScorecardMonitorIssue,
  ScorecardMonitorProbe,
  ScorecardMonitorRun,
  ScorecardMonitoredBusiness,
} from '../types';

export type ListMonitorIssuesParams = {
  severity?: string;
  source?: string;
  status?: string;
};

export type RTFWebhookEvent = components['schemas']['RTFWebhookEventRead'];

export async function listWebhookEvents(): Promise<RTFWebhookEvent[]> {
  const { data, error } = await getApiClient().GET('/scorecard-monitor/webhooks');
  if (error) throw new Error('Could not load partner webhooks. Try refreshing.');
  return data ?? [];
}

export async function listMonitoredBusinesses(): Promise<ScorecardMonitoredBusiness[]> {
  const client = getApiClient();
  const { data, error } = await client.GET('/scorecard-monitor/businesses');
  if (error) throw new Error(`Failed to fetch monitored businesses: ${JSON.stringify(error)}`);
  return data ?? [];
}

type CreateMonitorRunRequest = components['schemas']['ScorecardMonitorRunRequest'];

export async function createMonitorRun(): Promise<ScorecardMonitorRun> {
  const client = getApiClient();
  const body = {
    mode: 'manual',
    since_days: 30,
    force_full_recrawl: true,
  } satisfies CreateMonitorRunRequest;
  const { data, error } = await client.POST('/scorecard-monitor/runs', { body });
  if (error || !data) {
    throw new Error(`Failed to start monitor run: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function listMonitorRuns(): Promise<ScorecardMonitorRun[]> {
  const client = getApiClient();
  const { data, error } = await client.GET('/scorecard-monitor/runs');
  if (error) throw new Error(`Failed to fetch monitor runs: ${JSON.stringify(error)}`);
  return data ?? [];
}

export type ListMonitorProbesParams = NonNullable<
  operations['list_probes_scorecard_monitor_probes_get']['parameters']['query']
>;

export async function listMonitorProbes(
  params: ListMonitorProbesParams = {},
): Promise<ScorecardMonitorProbe[]> {
  const client = getApiClient();
  const query = {
    ...(typeof params.run_id === 'number' ? { run_id: params.run_id } : {}),
    ...(params.source ? { source: params.source } : {}),
    ...(params.status ? { status: params.status } : {}),
  } satisfies ListMonitorProbesParams;
  const { data, error } = await client.GET('/scorecard-monitor/probes', {
    params: { query },
  });
  if (error) throw new Error(`Failed to fetch monitor probes: ${JSON.stringify(error)}`);
  return data ?? [];
}

export async function listMonitorIssues(
  params: ListMonitorIssuesParams = {},
): Promise<ScorecardMonitorIssue[]> {
  const client = getApiClient();
  const query = {
    ...(params.severity ? { severity: params.severity } : {}),
    ...(params.source ? { source: params.source } : {}),
    ...(params.status ? { status: params.status } : {}),
  };
  const { data, error } = await client.GET('/scorecard-monitor/issues', {
    params: { query },
  });
  if (error) throw new Error(`Failed to fetch monitor issues: ${JSON.stringify(error)}`);
  return data ?? [];
}

export type ListMonitorIncidentsParams = NonNullable<
  operations['list_incidents_scorecard_monitor_incidents_get']['parameters']['query']
>;

export async function listMonitorIncidents(
  params: ListMonitorIncidentsParams = {},
): Promise<ScorecardIncident[]> {
  const client = getApiClient();
  const query = {
    ...(params.status ? { status: params.status } : {}),
    ...(typeof params.ongoing === 'boolean' ? { ongoing: params.ongoing } : {}),
  } satisfies ListMonitorIncidentsParams;
  const { data, error } = await client.GET('/scorecard-monitor/incidents', {
    params: { query },
  });
  if (error) throw new Error(`Failed to fetch monitor incidents: ${JSON.stringify(error)}`);
  return data ?? [];
}
